import type { ChallengeDifficulty } from '../core/types.js';
import { uuid } from '../core/ids.js';
import { filterModules } from './module-filter.js';
import { RESCUE_MODULES } from './rescue-registry.js';
import type {
  ChallengeContext, ChallengeEvaluation, ChallengeTemplate, GeneratedChallenge,
} from './challenge-types.js';

/**
 * Challenge templates per module. Keep this list small and safe — adding new
 * metrics should not require touching the module source. Safety tags filter
 * out templates that re-create the user's primary urge pattern (e.g. blackjack
 * for a gambling-domain plan).
 */
export const CHALLENGE_TEMPLATES: ChallengeTemplate[] = [
  // Infinity Breaker — score/wave/duration
  {
    id: 'infinity-breaker.score',
    moduleId: 'infinity-breaker',
    metric: { key: 'score', label: 'points', kind: 'number', detailsPath: 'score' },
    targets: { gentle: 1500, steady: 4000, stretch: 9000, last_chance: 15000 },
    promptTemplate: 'Score at least {target} points in Infinity Breaker.',
    reducedMotionSafe: false,
  },
  {
    id: 'infinity-breaker.wave',
    moduleId: 'infinity-breaker',
    metric: { key: 'wave', label: 'waves', kind: 'number', detailsPath: 'wave' },
    targets: { gentle: 2, steady: 4, stretch: 6, last_chance: 8 },
    promptTemplate: 'Reach wave {target} in Infinity Breaker.',
    reducedMotionSafe: false,
  },
  // Air Hockey
  {
    id: 'air-hockey.win',
    moduleId: 'air-hockey',
    metric: { key: 'win', label: 'match win', kind: 'boolean', detailsPath: 'win', fallbackToCompleted: false },
    targets: { gentle: true, steady: true, stretch: true, last_chance: true },
    promptTemplate: 'Win a match of Air Hockey.',
    reducedMotionSafe: false,
  },
  {
    id: 'air-hockey.goals',
    moduleId: 'air-hockey',
    metric: { key: 'goalsFor', label: 'goals', kind: 'number', detailsPath: 'goalsFor' },
    targets: { gentle: 1, steady: 3, stretch: 5, last_chance: 7 },
    promptTemplate: 'Score {target} goals in Air Hockey.',
    reducedMotionSafe: false,
  },
  // Pattern Pulse
  {
    id: 'pattern-pulse.rounds',
    moduleId: 'pattern-pulse',
    metric: { key: 'rounds', label: 'rounds', kind: 'number', detailsPath: 'rounds' },
    targets: { gentle: 2, steady: 4, stretch: 6, last_chance: 9 },
    promptTemplate: 'Clear {target} rounds of Pattern Pulse.',
    reducedMotionSafe: true,
  },
  // Just Breathe
  {
    id: 'breathing-orb.complete',
    moduleId: 'breathing-orb',
    metric: {
      key: 'completed', label: 'completion', kind: 'boolean',
      detailsPath: 'completed', fallbackToCompleted: true,
    },
    targets: { gentle: true, steady: true, stretch: true, last_chance: true },
    promptTemplate: 'Complete a full Just Breathe cycle.',
    reducedMotionSafe: true,
  },
  // Blackjack — exclude for gambling domain
  {
    id: 'blackjack.hands',
    moduleId: 'blackjack',
    metric: { key: 'handsPlayed', label: 'hands', kind: 'number', detailsPath: 'handsPlayed' },
    targets: { gentle: 3, steady: 6, stretch: 10, last_chance: 15 },
    promptTemplate: 'Play {target} hands of Blackjack.',
    excludeForDomains: ['gambling'],
    safetyTags: ['gambling-like'],
    reducedMotionSafe: true,
  },
];

function templateAllowed(t: ChallengeTemplate, ctx: ChallengeContext): boolean {
  if (t.excludeForDomains?.includes(ctx.domain)) return false;
  if (ctx.reducedMotion && !t.reducedMotionSafe) return false;
  if (ctx.excludeTemplateIds?.includes(t.id)) return false;
  // Module must exist and be available for the user's domain & reduced motion.
  const mod = RESCUE_MODULES.find((m) => m.id === t.moduleId);
  if (!mod) return false;
  const allowed = filterModules([mod], { domain: ctx.domain, reducedMotion: ctx.reducedMotion });
  return allowed.length > 0;
}

function pickDifficulty(ctx: ChallengeContext): ChallengeDifficulty {
  if (ctx.difficulty) return ctx.difficulty;
  // Escalate by repeat module use this episode.
  const moduleRuns = ctx.recentModuleIds?.length ?? 0;
  if (moduleRuns === 0) return 'steady';
  if (moduleRuns === 1) return 'stretch';
  return 'last_chance';
}

function renderPrompt(t: ChallengeTemplate, target: number | boolean): string {
  if (typeof target === 'boolean') return t.promptTemplate.replace('{target}', '');
  return t.promptTemplate.replace('{target}', String(target));
}

export function generateChallenge(ctx: ChallengeContext): GeneratedChallenge | null {
  const eligible = CHALLENGE_TEMPLATES.filter((t) => templateAllowed(t, ctx));
  if (eligible.length === 0) return null;
  // Prefer modules the user has not run yet this episode.
  const recent = new Set(ctx.recentModuleIds ?? []);
  const fresh = eligible.filter((t) => !recent.has(t.moduleId));
  const pool = fresh.length > 0 ? fresh : eligible;
  const choice = pool[Math.floor(Math.random() * pool.length)];
  if (!choice) return null;
  const difficulty = pickDifficulty(ctx);
  const target = choice.targets[difficulty];
  return {
    id: uuid(),
    template: choice,
    moduleId: choice.moduleId,
    metric: choice.metric.key,
    target,
    difficulty,
    prompt: renderPrompt(choice, target),
  };
}

function readMetric(details: Record<string, unknown> | undefined, path: string): unknown {
  if (!details) return undefined;
  // Simple dotless path for now.
  return details[path];
}

export function evaluateChallenge(
  challenge: GeneratedChallenge,
  runDetails: Record<string, unknown> | undefined,
  runCompleted: boolean,
  durationMs: number,
): ChallengeEvaluation {
  const m = challenge.template.metric;
  let actual: number | boolean | undefined;
  const raw = readMetric(runDetails, m.detailsPath);
  if (m.kind === 'number') {
    actual = typeof raw === 'number' ? raw : undefined;
  } else {
    if (typeof raw === 'boolean') actual = raw;
    else if (raw === undefined && m.fallbackToCompleted) actual = runCompleted;
  }
  let result: ChallengeEvaluation['result'] = 'not_met';
  if (m.kind === 'number') {
    const target = challenge.target as number;
    if (typeof actual === 'number' && actual >= target) result = 'met';
  } else {
    const target = challenge.target as boolean;
    if (actual === target) result = 'met';
  }
  const ev: ChallengeEvaluation = { challengeId: challenge.id, result, durationMs };
  if (actual !== undefined) ev.actual = actual;
  return ev;
}
